import Maximize from "@/assets/icons/maximize.svg";
import Minimize from "@/assets/icons/minimize.svg";
import { cn } from "@/lib/utils";
import mailStore, { setMailActionType } from "@/stores/mailStore";
import { useStore } from "@tanstack/react-store";
import { X } from "lucide-react";
import { RefObject } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import MailDestinationInputs from "./MailDestinationInputs";
import MessageTypeSelect from "./MessageTypeSelect";

interface MailInputHeaderProps {
    isFullScreen: Boolean;
    isExpanded: Boolean;
    closeDialog?: () => void;
    handleMaximizeToggle?: () => void;
    subject: string;
    setSubject: (subject: string) => void;
    toRecipientsState: [string[], (recipients: string[]) => void];
    ccRecipientsState: [string[], (recipients: string[]) => void];
    bccRecipientsState: [string[], (recipients: string[]) => void];
    toInputRef?: RefObject<HTMLInputElement | null>;
    showMessageTypeSelect?: boolean;
    showSubjectInput?: boolean;
    /** In a tab of the assistant's side panel: the tab closes it, no button of its own. */
    hideCornerButton?: boolean;
}

const MailInputHeader = ({
    isFullScreen = false,
    isExpanded = false,
    closeDialog,
    handleMaximizeToggle,
    subject,
    setSubject,
    toRecipientsState,
    ccRecipientsState,
    bccRecipientsState,
    toInputRef,
    showMessageTypeSelect = false,
    showSubjectInput = false,
    hideCornerButton = false,
}: MailInputHeaderProps) => {
    const mailActionType = useStore(mailStore, (state) => state.mailActionType);
    const { t } = useTranslation();

    const getIcon = () => {
        if (closeDialog) return <X className="text-primary-darker" />;

        return (
            <img
                alt=""
                className="h-5"
                src={isFullScreen ? Minimize : Maximize}
            />
        );
    };

    return (
        <div
            className={cn(
                "z-modal-backdrop",
                "grid auto-rows-auto",
                "h-0 overflow-hidden w-full",
                (isFullScreen || isExpanded) && "h-fit",
            )}
        >
            <div className={cn("grid grid-cols-[minmax(0,1fr),auto] w-full")}>
                <div
                    className={cn(
                        "grid grid-cols-[auto,minmax(0,1fr)]",
                        "min-h-10 gap-2 w-full",
                        isFullScreen && "py-2",
                    )}
                >
                    {showMessageTypeSelect && mailActionType && (
                        <div className="h-[27px] flex items-center">
                            <MessageTypeSelect
                                messageType={mailActionType}
                                setMessageType={setMailActionType}
                            />
                        </div>
                    )}
                    <MailDestinationInputs
                        bccRecipientsState={bccRecipientsState}
                        ccRecipientsState={ccRecipientsState}
                        className={cn(
                            !isFullScreen && isExpanded && "hidden lg:flex",
                        )}
                        toInputRef={toInputRef}
                        toRecipientsState={toRecipientsState}
                    />
                </div>

                {!hideCornerButton && (
                    <Button
                        className={cn(
                            "min-w-8 hover:bg-surface-textArea/25",
                            !isExpanded && !isFullScreen && "w-0 min-w-0",
                        )}
                        aria-label={t(
                            closeDialog
                                ? "common.close"
                                : isFullScreen
                                  ? "email.minimizeReply"
                                  : "email.maximizeReply",
                        )}
                        onClick={closeDialog || handleMaximizeToggle}
                        // Maximize keeps the focus in place so Enter keeps typing (not the dialog's close X).
                        onMouseDown={
                            closeDialog ? undefined : (e) => e.preventDefault()
                        }
                        size="smallCircle"
                        variant="invisible"
                    >
                        {getIcon()}
                    </Button>
                )}
            </div>

            {showSubjectInput && (
                <Input
                    className="text-bold py-2 text-xl border-y border-y-gray-100"
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder={t("email.subject")}
                    value={subject}
                    variant="ghost"
                />
            )}
        </div>
    );
};

export default MailInputHeader;
